import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Search, BookOpen } from "lucide-react";
import api from "../../lib/api";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loading, EmptyState } from "../shared";
import { CourseCard } from "./CourseCard";

const DURATIONS = [{ v: "all", l: "Any duration" }, { v: "60", l: "Up to 1h" }, { v: "120", l: "Up to 2h" }, { v: "240", l: "Up to 4h" }];

function F({ label, value, onChange, items, testId }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="w-full sm:w-44 h-9 text-sm bg-white" data-testid={testId}><SelectValue placeholder={label} /></SelectTrigger>
      <SelectContent>{items.map((it) => <SelectItem key={it.v} value={it.v}>{it.l}</SelectItem>)}</SelectContent>
    </Select>
  );
}

export function BrowseAll({ framework, actions, initialStage }) {
  const [q, setQ] = useState("");
  const [stage, setStage] = useState(initialStage || "all");
  const [competency, setCompetency] = useState("all");
  const [source, setSource] = useState("all");
  const [level, setLevel] = useState("all");
  const [dur, setDur] = useState("all");
  const params = { q: q || undefined, stage, competency, source, level, max_minutes: dur === "all" ? undefined : Number(dur) };
  const { data, isLoading } = useQuery({ queryKey: ["catalogue", params], queryFn: async () => (await api.get("/academy/catalogue", { params })).data, keepPreviousData: true });

  return (
    <div data-testid="browse-all">
      <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm p-4 mb-5 flex flex-col gap-3">
        <div className="relative">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search the cached catalogue — title, description or skill" className="pl-9 h-10" data-testid="browse-search" />
        </div>
        <div className="flex flex-wrap gap-2">
          <F label="Stage" value={stage} onChange={setStage} testId="filter-stage" items={[{ v: "all", l: "All stages" }, ...framework.stages.map((s) => ({ v: s.key, l: s.name }))]} />
          <F label="Competency" value={competency} onChange={setCompetency} testId="filter-competency" items={[{ v: "all", l: "All competencies" }, ...framework.competencies.map((c) => ({ v: c.key, l: c.name }))]} />
          <F label="Source" value={source} onChange={setSource} testId="filter-source" items={[{ v: "all", l: "All sources" }, { v: "sustek", l: "SusTek Academy" }, { v: "linkedin", l: "LinkedIn Learning" }]} />
          <F label="Level" value={level} onChange={setLevel} testId="filter-level" items={[{ v: "all", l: "All levels" }, ...framework.levels.map((l) => ({ v: l.key, l: l.label }))]} />
          <F label="Duration" value={dur} onChange={setDur} testId="filter-duration" items={DURATIONS} />
        </div>
      </div>
      {isLoading && !data ? <Loading /> : (data?.items?.length || 0) === 0 ? (
        <EmptyState icon={BookOpen} title="No courses match" description="Try a broader stage, competency or search term." />
      ) : (
        <>
          <div className="text-xs text-slate-500 mb-3" data-testid="browse-count">{data.total} course{data.total === 1 ? "" : "s"}</div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
            {data.items.map((c, i) => <CourseCard key={c.id} c={c} actions={actions} index={i} />)}
          </div>
        </>
      )}
    </div>
  );
}
